import { useState } from "react";
import { useSnapshot } from "valtio";
import GenerateIntroOverlay from "./components/GenerateIntroOverlay";
import GuestSelection from "./components/GuestSelection";
import SelectEvent from "./components/SelectEvent";
import SelectedGuestSummary from "./components/SelectedGuestSummary";
import { accessCardState } from "../../../stores/accessCardStore";
import { editorState } from "../../../utils/editor";
import { getEventTypeFromEditor } from "../../../utils/guest";
import { useGenerateAccessCards } from "../../../apis/wedding/generate.access.cards";

type GenerateStage = "intro" | "selectEvent" | "selectGuests" | "summary";

const GeneratePage = () => {
  const [currentStage, setCurrentStage] = useState<GenerateStage>("intro");
  const [selectedGuests, setSelectedGuests] = useState<Set<string>>(new Set());
  const { selectedEvent } = useSnapshot(accessCardState);
  const generateAccessCards = useGenerateAccessCards({
    onCompleted({ data }) {
      accessCardState.completeGeneration(data);
    },
    onError(error) {
      accessCardState.failGeneration(
        error instanceof Error
          ? error.message
          : "We couldn't generate the access cards. Please try again."
      );
    },
  });

  const handleGetStarted = () => {
    setCurrentStage("selectEvent");
  };

  const handleEventSelected = (eventId: string) => {
    // Use the existing utility function to get the event type
    const eventType = getEventTypeFromEditor(eventId, editorState);
    
    accessCardState.setSelectedEventWithDetails(eventId, eventType || "");
    setCurrentStage("selectGuests");
  };

  const handleGuestsSelected = (guestIds: Set<string>) => {
    setSelectedGuests(guestIds);
    setCurrentStage("summary");
  };

  const handleBackToEvents = () => {
    setCurrentStage("selectEvent");
  };

  const handleBackToGuests = () => {
    setCurrentStage("selectGuests");
  };

  const handleGenerate = () => {
    if (!selectedEvent || selectedGuests.size === 0) return;
    const guestIds = [...selectedGuests];
    accessCardState.startGeneration(guestIds);
    generateAccessCards.mutate({
      body: { eventId: selectedEvent, guestIds },
    });
  };

  const renderCurrentStage = () => {
    switch (currentStage) {
      case "intro":
        return (
          <GenerateIntroOverlay
            onGetStarted={handleGetStarted}
          />
        );
      case "selectEvent":
        return (
          <SelectEvent
            onEventSelected={handleEventSelected}
            selectedEvent={selectedEvent}
          />
        );
      case "selectGuests":
        return (
          <GuestSelection
            eventId={selectedEvent!}
            selectedGuests={selectedGuests}
            onGuestsSelected={handleGuestsSelected}
            onBack={handleBackToEvents}
          />
        );
      case "summary":
        return (
          <SelectedGuestSummary
            eventId={selectedEvent!}
            selectedGuests={selectedGuests}
            onBack={handleBackToGuests}
            onGenerate={handleGenerate}
            isGenerating={generateAccessCards.isPending}
          />
        );
      default:
        return <GenerateIntroOverlay onGetStarted={handleGetStarted} />;
    }
  };

  return <div className="mx-auto w-full px-1 pb-4 md:w-[70%]">{renderCurrentStage()}</div>;
};

export default GeneratePage;
